/**
 * Header Component - Top navigation bar with Injio-branded styling
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Avatar,
  MenuDivider,
  makeStyles,
  tokens,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from '@fluentui/react-components';
import {
  SignOut24Regular,
  Settings24Regular,
  Globe24Regular,
  Person24Regular,
  Delete24Regular,
  Alert24Regular,
  DocumentQuestionMark24Regular,
} from '@fluentui/react-icons';
import { ManageSitesPanel } from '../layout/ManageSitesPanel';
import {
  SP_LIST_NAME_EMBEDDINGS,
  SEARCH_INDEX_STALE_DAYS,
} from '../../config/constants';
import { SearchIndexerService } from '../../services/SearchIndexerService';
import { coreSettingsService } from '../../services/CoreSettingsService';

const useStyles = makeStyles({
  menuItem: {
    fontSize: tokens.fontSizeBase300,
  },
});

interface HeaderProps {
  userName?: string;
  isAuthenticated?: boolean;
  onSignIn?: () => void;
  onSignOut?: () => void;
  siteUrl?: string; // needed for Reset All Reports
  localIndexService?: { embeddingsService: any }; // from Dashboard/CopilotPanel
}

export default function Header({
  userName,
  isAuthenticated,
  onSignIn,
  onSignOut,
  siteUrl,
  localIndexService,
}: HeaderProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [isManageSitesOpen, setIsManageSitesOpen] = useState(false);
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const [lastSyncDate, setLastSyncDate] = useState<string | null>(null);
  const [hasCheckedSync, setHasCheckedSync] = useState(false);
  const [staleDaysThreshold, setStaleDaysThreshold] = useState<number>(
    SEARCH_INDEX_STALE_DAYS,
  );
  const indexServiceRef = useRef<SearchIndexerService | null>(null);

  useEffect(() => {
    if (!siteUrl || !isAuthenticated) return;
    if (!indexServiceRef.current)
      indexServiceRef.current = new SearchIndexerService();
    indexServiceRef.current
      .getLastSyncDate(siteUrl)
      .then((date) => {
        setLastSyncDate(date);
        setHasCheckedSync(true);
      })
      .catch(() => {
        setHasCheckedSync(true);
      });
    coreSettingsService
      .getSettings(siteUrl)
      .then((config) => {
        setStaleDaysThreshold(
          config.searchIndexStaleDays ?? SEARCH_INDEX_STALE_DAYS,
        );
      })
      .catch(() => {});
  }, [siteUrl, isAuthenticated]);

  // Distinguish "never indexed" (checked, no sync date at all) from "stale"
  // (has a sync date, but it's older than the configured threshold) — both
  // need attention, but the message and CTA differ.
  const neverIndexed = hasCheckedSync && !lastSyncDate;

  const isIndexStale = useMemo(() => {
    if (!lastSyncDate) return false;
    return (
      (Date.now() - new Date(lastSyncDate).getTime()) / 86_400_000 >
      staleDaysThreshold
    );
  }, [lastSyncDate, staleDaysThreshold]);

  const staleDaysText = useMemo(() => {
    if (!lastSyncDate) return '';
    const days = Math.floor(
      (Date.now() - new Date(lastSyncDate).getTime()) / 86_400_000,
    );
    return days === 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
  }, [lastSyncDate]);

  const needsAttention = isIndexStale || neverIndexed;

  const handleResetAllReports = async () => {
    if (!siteUrl || !localIndexService?.embeddingsService) return;
    setIsResetting(true);
    try {
      await localIndexService.embeddingsService.deleteAllRecords(
        siteUrl,
        SP_LIST_NAME_EMBEDDINGS,
      );
      console.log('[Header] Reset All Reports complete — reloading...');
      window.location.reload(); // trigger full re-scan on reload
    } catch (e) {
      console.error('[Header] Reset All Reports failed', e);
    } finally {
      setIsResetting(false);
      setIsResetOpen(false);
    }
  };

  const navLinks = [];

  if (isAuthenticated) {
    navLinks.push({ name: 'Dashboard', path: '/dashboard', public: false });
    navLinks.push({
      name: 'Search Indexer',
      path: '/tools/search-indexer',
      public: false,
    });
  } else {
    navLinks.push({ name: 'Home', path: '/', public: true });
  }

  return (
    <header className='bg-black text-white py-4 px-4 sm:px-6 lg:px-12 xl:px-20 shadow-md'>
      <div className='flex items-center justify-between'>
        {/* Logo */}
        <div className='flex items-center space-x-2'>
          <Link to='/'>
            <img
              src='/Injio_Logo.webp'
              alt='Injio Site Insights by WebVine'
              className='h-14 cursor-pointer'
            />
          </Link>
        </div>

        {/* Center - Navigation Links */}
        <nav className='hidden md:flex items-center gap-6 h-full'>
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-all ${
                  isActive
                    ? 'text-[#2ba6bb] border-b-2 border-[#2ba6bb] translate-y-[1px] pb-1'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            );
          })}
        </nav>

        {/* Right side - User info and auth buttons */}
        <div className='flex items-center gap-3'>
          {isAuthenticated ? (
            <>
              {/* Notification Bell */}
              <Popover
                open={isAlertsOpen}
                onOpenChange={(_, d) => setIsAlertsOpen(d.open)}
                positioning='below-end'
              >
                <PopoverTrigger disableButtonEnhancement>
                  <button
                    type='button'
                    aria-label={
                      needsAttention
                        ? 'Notifications (1 unread)'
                        : 'Notifications'
                    }
                    className='relative p-1.5 rounded-full text-gray-300 hover:text-white hover:bg-white/10 transition-all focus:outline-none focus:ring-2 focus:ring-[#2ba6bb]/50'
                  >
                    <Alert24Regular style={{ width: 22, height: 22 }} />
                    {needsAttention && (
                      <span
                        aria-hidden='true'
                        className='absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#2ba6bb] text-white text-[10px] font-bold flex items-center justify-center leading-none'
                      >
                        1
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverSurface
                  style={{
                    padding: 0,
                    background: '#0f172a',
                    border: '1px solid rgba(43,166,187,0.25)',
                    borderTop: '3px solid #2ba6bb',
                    borderRadius: '0 0 10px 10px',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
                    width: '300px',
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ padding: '14px 16px 16px' }}>
                    <p
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.40)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        marginBottom: '10px',
                        marginTop: 0,
                      }}
                    >
                      Notifications
                    </p>
                    <div
                      style={{
                        background: 'rgba(43,166,187,0.10)',
                        border: '1px solid rgba(43,166,187,0.30)',
                        borderLeft: '3px solid #2ba6bb',
                        borderRadius: '8px',
                        padding: '10px 12px',
                      }}
                    >
                      <p
                        style={{
                          fontSize: '13px',
                          color: 'rgba(255,255,255,0.85)',
                          lineHeight: 1.6,
                          margin: 0,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 900,
                            color: '#2ba6bb',
                            marginRight: '4px',
                          }}
                        >
                          ALERT:
                        </span>
                        {neverIndexed
                          ? "Search index hasn't been built yet. Run an initial index to enable semantic search."
                          : isIndexStale
                            ? `Search index is out of date${staleDaysText ? ` (Last updated ${staleDaysText})` : ''}.`
                            : 'All search indexes are up to date.'}
                      </p>
                      <button
                        type='button'
                        onClick={() => {
                          navigate('/tools/search-indexer?tab=index');
                          setIsAlertsOpen(false);
                        }}
                        style={{
                          marginTop: '10px',
                          padding: '6px 14px',
                          background: '#2ba6bb',
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: 700,
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'inline-block',
                          fontFamily: 'inherit',
                          transition: 'background 0.15s',
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.background = '#228a9d')
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.background = '#2ba6bb')
                        }
                      >
                        {neverIndexed ? 'Build Index Now' : 'Re-index Now'}
                      </button>
                    </div>
                  </div>
                </PopoverSurface>
              </Popover>

              {/* Vertical divider between bell and welcome */}
              <div
                aria-hidden='true'
                className='hidden lg:block w-px h-6 bg-white/20 mx-1'
              />

              <span className='text-sm hidden lg:inline mr-2 text-gray-300'>
                {userName ? (
                  <>
                    Welcome,{' '}
                    <span className='font-semibold text-white'>{userName}</span>
                  </>
                ) : (
                  'Welcome'
                )}
              </span>

              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Avatar
                    className='cursor-pointer'
                    name={userName}
                    color='brand'
                    size={32}
                    aria-label='User Profile'
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      icon={<Person24Regular />}
                      className={styles.menuItem}
                      disabled
                    >
                      Signed in as {userName}
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem
                      icon={<Settings24Regular />}
                      className={styles.menuItem}
                      onClick={() => navigate('/admin/settings')}
                    >
                      Settings
                    </MenuItem>
                    <MenuItem
                      icon={<Globe24Regular />}
                      className={styles.menuItem}
                      onClick={() => setIsManageSitesOpen(true)}
                    >
                      Manage Sites
                    </MenuItem>
                    <MenuItem
                      icon={<Delete24Regular />}
                      className={styles.menuItem}
                      style={{ color: tokens.colorPaletteRedForeground1 }}
                      onClick={() => setIsResetOpen(true)}
                    >
                      Reset All Reports
                    </MenuItem>
                    <MenuItem
                      icon={<DocumentQuestionMark24Regular />}
                      className={styles.menuItem}
                      onClick={() =>
                        window.open(
                          'https://webvine.atlassian.net/servicedesk/customer/portals',
                          '_blank',
                          'noopener,noreferrer',
                        )
                      }
                    >
                      Help
                    </MenuItem>

                    <MenuDivider />
                    <MenuItem
                      icon={<SignOut24Regular />}
                      className={styles.menuItem}
                      onClick={onSignOut}
                    >
                      Sign Out
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>

              <ManageSitesPanel
                isOpen={isManageSitesOpen}
                onDismiss={() => setIsManageSitesOpen(false)}
              />

              {/* Reset All Reports — confirm dialog */}
              <Dialog
                open={isResetOpen}
                onOpenChange={(_, d) => {
                  if (!isResetting) setIsResetOpen(d.open);
                }}
              >
                <DialogSurface>
                  <DialogBody>
                    <DialogTitle>Reset All Analysis Reports?</DialogTitle>
                    <DialogContent>
                      This will permanently delete all saved scores and quality
                      checks from SharePoint and clear the local cache. Pages
                      will be re-scanned on next load.
                    </DialogContent>
                    <DialogActions>
                      <DialogTrigger disableButtonEnhancement>
                        <Button appearance='secondary' disabled={isResetting}>
                          Cancel
                        </Button>
                      </DialogTrigger>
                      <Button
                        appearance='primary'
                        style={{
                          backgroundColor: tokens.colorPaletteRedBackground3,
                        }}
                        onClick={handleResetAllReports}
                        disabled={isResetting}
                        icon={
                          isResetting ? (
                            <Spinner size='tiny' />
                          ) : (
                            <Delete24Regular />
                          )
                        }
                      >
                        {isResetting ? 'Deleting…' : 'Delete & Reset'}
                      </Button>
                    </DialogActions>
                  </DialogBody>
                </DialogSurface>
              </Dialog>
            </>
          ) : (
            <button
              onClick={onSignIn}
              className='px-5 py-2.5 rounded-lg transition-all duration-200 font-semibold text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 bg-white text-[#2ba6bb] hover:bg-gray-50'
            >
              Sign In
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
